import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { ApiError, NetworkError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { clientsKey, listClients } from '../inventory/api';
import { CameraAccess } from './CameraAccess';
import {
  createUser,
  fieldMessages,
  resendInvite,
  updateUser,
  usersKey,
  type Member,
  isClientKind,
  type Role,
  type UpdateUserBody,
} from './api';

interface Props {
  /** Undefined opens the drawer in "create" mode. */
  member?: Member;
  roles: Role[];
  onClose: () => void;
}

interface Problem {
  fields: Record<string, string>;
  form: string | null;
}

const noProblem: Problem = { fields: {}, form: null };

function toProblem(err: unknown): Problem {
  if (err instanceof ApiError) {
    if (err.status === 422) return { fields: fieldMessages(err.fields), form: null };
    if (err.status === 409 && err.code === 'last_admin')
      return {
        fields: {},
        form: 'A organização precisa manter pelo menos um administrador ativo.',
      };
    if (err.status === 403) return { fields: {}, form: 'Você não tem permissão para esta ação.' };
    if (err.status === 404) return { fields: {}, form: 'Este usuário não existe mais.' };
    if (err.code === 'mail_unavailable')
      return {
        fields: {},
        form: 'O envio de e-mails não está configurado nesta instalação. Use "Definir a senha agora".',
      };
    if (err.code === 'not_invited')
      return { fields: {}, form: 'Este usuário já tem senha: não há convite para reenviar.' };
  }
  if (err instanceof NetworkError)
    return { fields: {}, form: 'Não foi possível contatar o servidor. Tente novamente.' };
  return { fields: {}, form: 'Ocorreu um erro inesperado. Tente novamente.' };
}

export function UserDrawer({ member, roles, onClose }: Props) {
  // A new client user goes straight to choosing its cameras.
  const [created, setCreated] = useState<Member | null>(null);
  const shown = member ?? created;
  return shown ? (
    <EditUser key={shown.id} member={shown} roles={roles} onClose={onClose} />
  ) : (
    <CreateUser roles={roles} onClose={onClose} onClientCreated={setCreated} />
  );
}

function CreateUser({
  roles,
  onClose,
  onClientCreated,
}: {
  roles: Role[];
  onClose: () => void;
  onClientCreated: (m: Member) => void;
}) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // An invitation lets the person choose the password (ADR-030).
  const [mode, setMode] = useState<'invite' | 'password'>('invite');
  const [selected, setSelected] = useState<string[]>([]);
  const [clientId, setClientId] = useState('');
  const needsClient = isClientKind(roles.find((r) => r.key === selected[0])?.kind);
  const [problem, setProblem] = useState<Problem>(noProblem);

  const mutation = useMutation({
    mutationFn: () =>
      createUser(request, {
        email,
        display_name: name,
        ...(mode === 'invite' ? { invite: true } : { password }),
        roles: selected,
        ...(needsClient && clientId ? { client_id: clientId } : {}),
      }),
    onSuccess: async (m) => {
      await qc.invalidateQueries({ queryKey: usersKey });
      const kind = roles.find((r) => r.key === m.roles[0]?.key)?.kind;
      const created = m.invited
        ? `Convite enviado para ${m.email}.`
        : `Usuário ${m.display_name} criado.`;
      if (isClientKind(kind)) {
        notify(`${created} Escolha as câmeras que ele verá.`);
        onClientCreated(m);
        return;
      }
      notify(created);
      onClose();
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    mutation.mutate();
  };

  return (
    <Drawer
      title="Novo usuário"
      description={
        mode === 'invite'
          ? 'A pessoa recebe um convite por e-mail, confirma um código e escolhe a própria senha.'
          : 'O usuário poderá entrar imediatamente com a senha definida.'
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" form="create-user" disabled={mutation.isPending}>
            {mutation.isPending
              ? 'Criando…'
              : mode === 'invite'
                ? 'Enviar convite'
                : 'Criar usuário'}
          </Button>
        </>
      }
    >
      <form id="create-user" onSubmit={submit} noValidate className="space-y-4">
        <FormProblem message={problem.form} />
        <Field label="Nome" error={problem.fields.display_name}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="E-mail" error={problem.fields.email}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
        <fieldset>
          <legend className="mb-1.5 text-xs font-medium text-fg-muted">Senha</legend>
          <div className="space-y-1.5">
            {(
              [
                [
                  'invite',
                  'Enviar convite por e-mail',
                  'A pessoa confirma um código enviado ao e-mail e escolhe a senha. Recomendado.',
                ],
                [
                  'password',
                  'Definir a senha agora',
                  'Você escolhe a senha e a repassa por um canal seguro.',
                ],
              ] as const
            ).map(([value, label, hint]) => (
              <label
                key={value}
                className="flex cursor-pointer items-start gap-2.5 rounded-md border border-line px-3 py-2 has-checked:border-accent/60 has-checked:bg-accent-soft"
              >
                <input
                  type="radio"
                  name="password-mode"
                  value={value}
                  checked={mode === value}
                  onChange={() => {
                    setMode(value);
                  }}
                  className="mt-0.5 accent-accent-strong"
                />
                <span>
                  <span className="block text-[13px] text-fg">{label}</span>
                  <span className="block text-xs text-fg-muted">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {mode === 'password' && (
          <Field
            label="Senha inicial"
            hint="Mínimo de 12 caracteres. Compartilhe por um canal seguro."
            error={problem.fields.password}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              />
            )}
          </Field>
        )}
        <RolePicker
          roles={roles}
          value={selected}
          onChange={setSelected}
          error={problem.fields.roles}
        />
        {needsClient && (
          <ClientPicker value={clientId} onChange={setClientId} error={problem.fields.client_id} />
        )}
      </form>
    </Drawer>
  );
}

function EditUser({
  member,
  roles,
  onClose,
}: {
  member: Member;
  roles: Role[];
  onClose: () => void;
}) {
  const { request, session } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const isSelf = session?.user.id === member.id;

  const [current, setCurrent] = useState(member);
  const [name, setName] = useState(member.display_name);
  const [selected, setSelected] = useState(member.roles.map((r) => r.key));
  const [clientId, setClientId] = useState(member.client?.id ?? '');
  const role = roles.find((r) => r.key === selected[0]);
  const needsClient = isClientKind(role?.kind);
  const clientChanged = needsClient && clientId !== '' && clientId !== (current.client?.id ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [problem, setProblem] = useState<Problem>(noProblem);
  const [confirm, setConfirm] = useState<'disable' | 'password' | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateUserBody) => updateUser(request, member.id, body),
    onSuccess: async (m, body) => {
      setCurrent(m);
      setConfirm(null);
      await qc.invalidateQueries({ queryKey: usersKey });
      if (body.password) {
        setNewPassword('');
        notify(`Senha de ${m.display_name} redefinida. Todas as sessões foram encerradas.`);
      } else if (body.status === 'disabled') {
        notify(`Acesso de ${m.display_name} desativado. Todas as sessões foram encerradas.`);
      } else if (body.status === 'active') {
        notify(`Acesso de ${m.display_name} reativado.`);
      } else {
        notify('Alterações salvas.');
      }
    },
    onError: (err) => {
      setConfirm(null);
      setProblem(toProblem(err));
    },
  });

  const run = (body: UpdateUserBody) => {
    setProblem(noProblem);
    mutation.mutate(body);
  };

  const resend = useMutation({
    mutationFn: () => resendInvite(request, member.id),
    onSuccess: (m) => {
      setCurrent(m);
      notify(`Novo convite enviado para ${m.email}. O link anterior deixou de valer.`);
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const sortedCurrent = current.roles.map((r) => r.key).sort();
  const dirty =
    name.trim() !== current.display_name ||
    [...selected].sort().join() !== sortedCurrent.join() ||
    clientChanged;

  const save = (e: SyntheticEvent) => {
    e.preventDefault();
    const body: UpdateUserBody = {};
    if (name.trim() !== current.display_name) body.display_name = name;
    if ([...selected].sort().join() !== sortedCurrent.join()) body.roles = selected;
    if (clientChanged) body.client_id = clientId;
    run(body);
  };

  return (
    <Drawer
      title={current.display_name}
      description={current.email}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Fechar</Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-user"
            disabled={!dirty || mutation.isPending}
          >
            {mutation.isPending && !confirm ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </>
      }
    >
      <form id="edit-user" onSubmit={save} noValidate className="space-y-4">
        <FormProblem message={problem.form} />
        <Field label="Nome" error={problem.fields.display_name}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="E-mail" hint="O e-mail identifica o usuário e não pode ser alterado.">
          {({ id, describedBy }) => (
            <input
              id={id}
              value={current.email}
              readOnly
              aria-describedby={describedBy}
              className={`${inputClass} text-fg-muted`}
            />
          )}
        </Field>
        <RolePicker
          roles={roles}
          value={selected}
          onChange={setSelected}
          error={problem.fields.roles}
        />
        {needsClient && (
          <ClientPicker value={clientId} onChange={setClientId} error={problem.fields.client_id} />
        )}
      </form>

      <CameraAccess
        userId={member.id}
        name={current.display_name}
        isAdmin={current.roles.some((r) => r.key === 'tenant_admin')}
        kind={roles.find((r) => r.key === current.roles[0]?.key)?.kind}
        client={current.client ?? null}
      />

      <section aria-labelledby="access-heading" className="mt-6 border-t border-line pt-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 id="access-heading" className="text-[13px] font-medium text-fg">
              Acesso
            </h3>
            <p className="mt-0.5 text-xs text-fg-muted">
              {isSelf
                ? 'Você não pode desativar a sua própria conta.'
                : current.invited && current.status === 'active'
                  ? 'O convite ainda não foi aceito: a pessoa não consegue entrar até criar a senha.'
                  : current.status === 'active'
                    ? 'Desativar encerra todas as sessões imediatamente.'
                    : 'O usuário não consegue entrar enquanto estiver desativado.'}
            </p>
          </div>
          <UserStatusBadge member={current} />
        </div>
        {current.invited && current.status === 'active' && (
          <div className="mt-3">
            <Button
              onClick={() => {
                setProblem(noProblem);
                resend.mutate();
              }}
              disabled={resend.isPending}
            >
              {resend.isPending ? 'Enviando…' : 'Reenviar convite'}
            </Button>
          </div>
        )}
        {problem.fields.status && <p className="mt-2 text-xs text-crit">{problem.fields.status}</p>}
        {!isSelf && (
          <div className="mt-3">
            {current.status === 'active' ? (
              <Button
                onClick={() => {
                  setConfirm('disable');
                }}
                className="text-crit!"
              >
                Desativar usuário
              </Button>
            ) : (
              <Button
                onClick={() => {
                  run({ status: 'active' });
                }}
                disabled={mutation.isPending}
              >
                Reativar usuário
              </Button>
            )}
          </div>
        )}
      </section>

      <section aria-labelledby="password-heading" className="mt-6 border-t border-line pt-5">
        <h3 id="password-heading" className="text-[13px] font-medium text-fg">
          Redefinir senha
        </h3>
        {isSelf ? (
          <p className="mt-0.5 text-xs text-fg-muted">
            Para alterar a sua própria senha, use “Alterar senha” no menu da conta.
          </p>
        ) : (
          <form
            className="mt-3 flex items-start gap-2"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setConfirm('password');
            }}
          >
            <div className="flex-1">
              <Field label="Nova senha" error={problem.fields.password}>
                {({ id, describedBy, invalid }) => (
                  <input
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                    }}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    className={inputClass}
                  />
                )}
              </Field>
            </div>
            <Button
              type="submit"
              className="mt-[22px]"
              disabled={!newPassword || mutation.isPending}
            >
              Redefinir
            </Button>
          </form>
        )}
      </section>

      {confirm === 'disable' && (
        <ConfirmDialog
          title={`Desativar ${current.display_name}?`}
          confirmLabel="Desativar"
          busy={mutation.isPending}
          onCancel={() => {
            setConfirm(null);
          }}
          onConfirm={() => {
            run({ status: 'disabled' });
          }}
        >
          O usuário será desconectado de todas as sessões e não conseguirá entrar até ser reativado.
        </ConfirmDialog>
      )}
      {confirm === 'password' && (
        <ConfirmDialog
          title="Redefinir a senha?"
          confirmLabel="Redefinir senha"
          busy={mutation.isPending}
          onCancel={() => {
            setConfirm(null);
          }}
          onConfirm={() => {
            run({ password: newPassword });
          }}
        >
          Todas as sessões de {current.display_name} serão encerradas e a nova senha passa a valer
          imediatamente.
        </ConfirmDialog>
      )}
    </Drawer>
  );
}

/** One profile per user (ADR-027): staff first, then people of a client. */
function RolePicker({
  roles,
  value,
  onChange,
  error,
}: {
  roles: Role[];
  value: string[];
  onChange: (keys: string[]) => void;
  error?: string | undefined;
}) {
  const groups = [
    { label: 'Equipe da empresa', items: roles.filter((r) => r.kind === 'org') },
    { label: 'Pessoas do cliente', items: roles.filter((r) => isClientKind(r.kind)) },
  ].filter((g) => g.items.length > 0);
  return (
    <fieldset aria-describedby={error ? 'roles-error' : undefined}>
      <legend className="mb-1.5 text-xs font-medium text-fg-muted">Perfil</legend>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.label}>
            <p className="mb-1 text-2xs tracking-wide text-fg-subtle uppercase">{g.label}</p>
            <div className="divide-y divide-line overflow-hidden rounded-md border border-line-strong">
              {g.items.map((r) => (
                <label
                  key={r.key}
                  className="flex cursor-pointer items-start gap-3 bg-canvas px-3 py-2.5 hover:bg-surface-hover"
                >
                  <input
                    type="radio"
                    name="profile"
                    checked={value[0] === r.key}
                    onChange={() => {
                      onChange([r.key]);
                    }}
                    className="mt-0.5 size-4 accent-[var(--color-accent-strong)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] text-fg">{r.name}</span>
                    {r.description && (
                      <span className="block text-xs text-fg-subtle">{r.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p id="roles-error" className="mt-1.5 text-xs text-crit">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/**
 * The client a person of a client belongs to (ADR-027). People of a client
 * who create users never see it: their own client is always used.
 */
function ClientPicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (id: string) => void;
  error?: string | undefined;
}) {
  const { request, session } = useAuth();
  const own = Boolean(session?.user.client_id);
  const clients = useQuery({
    queryKey: [...clientsKey, 'picker'],
    queryFn: async () => (await listClients(request, { page: 1, size: 100 })).items,
    enabled: !own,
  });
  if (own) return null;
  return (
    <Field
      label="Cliente"
      hint="A pessoa só verá câmeras deste cliente. Mudar o cliente apaga as câmeras liberadas."
      error={error}
    >
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={inputClass}
        >
          <option value="" disabled>
            {clients.isPending ? 'Carregando clientes…' : 'Escolha o cliente'}
          </option>
          {clients.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/** Status of a user, including a pending invitation (ADR-030). */
export function UserStatusBadge({ member }: { member: Member }) {
  if (member.status !== 'active') return <StatusBadge tone="neutral">Desativado</StatusBadge>;
  if (member.invited) return <StatusBadge tone="warn">Convite pendente</StatusBadge>;
  return <StatusBadge tone="ok">Ativo</StatusBadge>;
}
